import React, { createContext, useCallback, useContext, useEffect, useReducer } from 'react'
import PropTypes from 'prop-types'
import secretsApi from 'api/secrets'
import crmApi from 'api/crm'
import { codifyAPIError } from 'utils/helpers'
import './types'

// Action Types
const ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_INTEGRATIONS: 'SET_INTEGRATIONS',
  SET_INTEGRATION_TYPES: 'SET_INTEGRATION_TYPES',
  ADD_INTEGRATION: 'ADD_INTEGRATION',
  UPDATE_INTEGRATION: 'UPDATE_INTEGRATION',
  DELETE_INTEGRATION: 'DELETE_INTEGRATION',
  RESET_STATE: 'RESET_STATE',
  SET_OPERATION_LOADING: 'SET_OPERATION_LOADING',
  SET_OPERATION_ERROR: 'SET_OPERATION_ERROR',
  SET_SUPPORTED_CRMS: 'SET_SUPPORTED_CRMS',
}

/** @type {DataIntegrationsState} */
const initialState = {
  integrationTypes: [],
  integrations: [],
  supportedCRMs: [],
  loading: {
    list: false,
    add: false,
    delete: {},
  },
  error: {
    list: null,
    add: null,
    delete: {},
  },
}

// Reducer
function reducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_OPERATION_LOADING:
      return {
        ...state,
        loading: {
          ...state.loading,
          [action.payload.operation]: action.payload.isLoading,
        },
        error: {
          ...state.error,
          [action.payload.operation]: null,
        },
      }
    case ACTIONS.SET_OPERATION_ERROR:
      return {
        ...state,
        loading: {
          ...state.loading,
          [action.payload.operation]: false,
        },
        error: {
          ...state.error,
          [action.payload.operation]: action.payload.error,
        },
      }
    case ACTIONS.SET_INTEGRATION_TYPES:
      return {
        ...state,
        integrationTypes: action.payload,
        loading: {
          ...state.loading,
          list: false,
        },
        error: {
          ...state.error,
          list: null,
        },
      }
    case ACTIONS.SET_INTEGRATIONS:
      return {
        ...state,
        integrations: action.payload,
        loading: {
          ...state.loading,
          list: false,
        },
        error: {
          ...state.error,
          list: null,
        },
      }
    case ACTIONS.ADD_INTEGRATION:
      return {
        ...state,
        integrations: [action.payload, ...state.integrations],
        loading: {
          ...state.loading,
          add: false,
        },
        error: {
          ...state.error,
          add: null,
        },
      }
    case ACTIONS.UPDATE_INTEGRATION:
      return {
        ...state,
        integrations: state.integrations.map((integration) =>
          integration.id === action.payload.id ? action.payload : integration
        ),
        loading: false,
        error: null,
      }
    case ACTIONS.DELETE_INTEGRATION:
      return {
        ...state,
        integrations: state.integrations.filter((integration) => integration.id !== action.payload.id),
        loading: {
          ...state.loading,
          delete: {
            ...state.loading.delete,
            [action.payload.id]: false,
          },
        },
        error: {
          ...state.error,
          delete: {
            ...state.error.delete,
            [action.payload.id]: null,
          },
        },
      }
    case ACTIONS.RESET_STATE:
      return initialState
    case ACTIONS.SET_SUPPORTED_CRMS:
      return {
        ...state,
        supportedCRMs: action.payload,
        loading: {
          ...state.loading,
          list: false,
        },
        error: {
          ...state.error,
          list: null,
        },
      }
    default:
      throw new Error(`Unknown action type: ${action.type}`)
  }
}

/** @type {React.Context<DataIntegrationsState>} */
const IntegrationsContext = createContext(null)

/** @type {React.Context<DataIntegrationsActions>} */
const IntegrationsDispatchContext = createContext(null)

/**
 * @param {Object} props
 * @param {React.ReactNode} props.children
 */
export default function DataIntegrationsProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState)

  /**
   * @param {Function} asyncFn
   * @param {string} operation
   * @param {string|null} id
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  const handleAsyncAction = useCallback(async (asyncFn, operation, id = null) => {
    try {
      dispatch({ 
        type: ACTIONS.SET_OPERATION_LOADING, 
        payload: { 
          operation: id ? `${operation}.${id}` : operation,
          isLoading: true 
        } 
      })
      const result = await asyncFn()
      if (result.error) {
        const errorCode = codifyAPIError(result.error)
        dispatch({ 
          type: ACTIONS.SET_OPERATION_ERROR, 
          payload: { 
            operation: id ? `${operation}.${id}` : operation,
            error: errorCode || 'common.genericError',
          } 
        })
        return { success: false, error: result.error }
      }
      return { success: true, data: result.data }
    } catch (error) {
      console.error('Error in async action:', error)
      const errorMessage = error.response?.data?.message || 'An error occurred'
      dispatch({ 
        type: ACTIONS.SET_OPERATION_ERROR, 
        payload: { 
          operation: id ? `${operation}.${id}` : operation,
          error: errorMessage 
        } 
      })
      return { success: false, error: errorMessage }
    }
  }, [])

  /**
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  const fetchIntegrationTypes = useCallback(async () => {
    const result = await handleAsyncAction(async () => {
      const response = await secretsApi.fetchServiceTypesAsync()
      dispatch({ type: ACTIONS.SET_INTEGRATION_TYPES, payload: response.data })
      return response
    }, 'list')
    return result
  }, [handleAsyncAction])
  
  /**
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  const fetchIntegrations = useCallback(async () => {
    const result = await handleAsyncAction(async () => {
      const response = await secretsApi.fetchSecretsAsync()
      dispatch({ type: ACTIONS.SET_INTEGRATIONS, payload: response.data })
      return response
    }, 'list')
    return result
  }, [handleAsyncAction])

  /**
   * @param {{serviceName: string, name: string, rawSecret: string, tenantRef: string}} params
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  const addIntegration = useCallback(async ({ serviceName, name, rawSecret, tenantRef }) => {
    const result = await handleAsyncAction(async () => {
      const response = await secretsApi.createSecretAsync({
        service_name: serviceName,
        name,
        raw_secret: rawSecret,
        tenant_ref: tenantRef,
      })
      return response
    }, 'add')
    if (result.success) {
      dispatch({ type: ACTIONS.ADD_INTEGRATION, payload: result.data })
    }
    return result
  }, [handleAsyncAction])

  /**
   * @param {string} integrationId
   * @param {Object} params
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  const updateIntegration = useCallback(async (integrationId, params) => {
    const result = await handleAsyncAction(async () => {
      const response = await secretsApi.updateIntegration(integrationId, params)
      dispatch({ type: ACTIONS.UPDATE_INTEGRATION, payload: response.data })
      return response
    })
    return result
  }, [handleAsyncAction])

  /**
   * @param {string} integrationId
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  const deleteIntegration = useCallback(async (integrationId) => {
    const result = await handleAsyncAction(async () => {
      const response = await secretsApi.deleteSecretAsync(integrationId)
      dispatch({ type: ACTIONS.DELETE_INTEGRATION, payload: { id: integrationId } })
      return response
    }, 'delete', integrationId)
    return result
  }, [handleAsyncAction])

  const resetState = useCallback(() => {
    dispatch({ type: ACTIONS.RESET_STATE })
  }, [])

  /**
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  const fetchSupportedCRMs = useCallback(async () => {
    const result = await handleAsyncAction(async () => {
      const response = await crmApi.fetchSupportedAsync()
      dispatch({ type: ACTIONS.SET_SUPPORTED_CRMS, payload: response.data })
      return response
    }, 'list')
    return result
  }, [handleAsyncAction])

  /**
   * @param {{crm_name: string, secret_id: string, container_name: string, nlq_id: string, ids: string[]}} params
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  const exportNLQToCRM = useCallback(async ({ crm_name, secret_id, container_name, nlq_id, ids }) => {
    const result = await handleAsyncAction(async () => {
      const response = await crmApi.exportNLQToCRMAsync({
        crm_name,
        secret_id,
        container_name,
        nlq_id,
        ids,
      })
      return response
    }, 'export')
    return result
  }, [handleAsyncAction])

  /**
   * @param {{crm_name: string, secret_id: string, container_name: string, packageId: string, tc: string}} params
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  const exportBackhaulPackageToCRM = useCallback(async ({ crm_name, secret_id, container_name, packageId, tc }) => {
    const result = await handleAsyncAction(async () => {
      const response = await crmApi.exportBackhaulPackageToCRMAsync({
        crm_name,
        secret_id,
        container_name,
        packageId,
        tc,
      })
      return response
    }, 'export')
    return result
  }, [handleAsyncAction])

  /**
   * @returns {Integration[]}
   */
  const getSupportedCRMs = useCallback(() => {
    const supportedCRMNames = state.supportedCRMs.map(crm => crm.type)
    return state.integrations.filter(integration => 
      supportedCRMNames.includes(integration.service_name)
    )
  }, [state.supportedCRMs, state.integrations])

  useEffect(() => {
    const fetchData = async () => {
      await fetchIntegrationTypes()
      await fetchIntegrations()
      await fetchSupportedCRMs()
    }
    fetchData()
  }, [fetchIntegrationTypes, fetchIntegrations, fetchSupportedCRMs])

  /** @type {DataIntegrationsActions} */
  const actions = {
    fetchIntegrations,
    addIntegration,
    updateIntegration,
    deleteIntegration,
    resetState,
    fetchIntegrationTypes,
    exportNLQToCRM,
    exportBackhaulPackageToCRM,
    getSupportedCRMs,
  }

  return (
    <IntegrationsContext.Provider value={state}>
      <IntegrationsDispatchContext.Provider value={actions}>
        {children}
      </IntegrationsDispatchContext.Provider>
    </IntegrationsContext.Provider>
  )
}

DataIntegrationsProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

/**
 * @returns {DataIntegrationsState}
 */
export function useIntegrationsState() {
  const context = useContext(IntegrationsContext)
  if (context === undefined) {
    throw new Error('useIntegrationsState must be used within an IntegrationsProvider')
  }
  return context
}

/**
 * @returns {DataIntegrationsActions}
 */
export function useIntegrationsActions() {
  const context = useContext(IntegrationsDispatchContext)
  if (context === undefined) {
    throw new Error('useIntegrationsActions must be used within an IntegrationsProvider')
  }
  return context
}

/**
 * @returns {DataIntegrationsContext}
 */
export function useDataIntegrations() {
  return {
    ...useIntegrationsState(),
    ...useIntegrationsActions(),
  }
} 
